import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaCamera,
  FaEdit,
  FaSave,
  FaTimes,
  FaEnvelope,
  FaGraduationCap,
  FaIdCard,
  FaCalendarAlt,
  FaUser,
  FaChartLine,
  FaRocket,
  FaAward,
  FaFire,
  FaUpload,
  FaHeart,
  FaEye,
  FaShieldAlt,
  FaStar,
  FaTrophy,
  FaBook,
  FaUsers,
  FaThumbsUp,
} from "react-icons/fa";
import {
  FiUploadCloud,
  FiDownload,
  FiBookOpen,
  FiTrendingUp,
  FiActivity,
} from "react-icons/fi";
import { HiOutlineSparkles } from "react-icons/hi";
import { useDropzone } from "react-dropzone";
import axios from "axios";
import toast from "react-hot-toast";
import Loader from "../components/common/Loader";
import { updateUser } from "../store/slices/authSlice";
import { useFooter } from "../context/FooterContext";
import { buildApiUrl } from "../utils/apiConfig";

const Profile = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { setShowFooter } = useFooter();
  const { user: currentUser, isAuthenticated } = useSelector(
    (state) => state.auth,
  );
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [editForm, setEditForm] = useState({
    name: "",
    branch: "",
    semester: "",
    scheme: "",
  });

  // Stats state
  const [stats, setStats] = useState({
    totalUploads: 0,
    totalDownloads: 0,
    totalLikes: 0,
    totalViews: 0,
    pendingCount: 0,
    approvedCount: 0,
    rejectedCount: 0,
  });

  const branches = ["CSE", "ISE", "ECE", "EEE", "MECH", "CIVIL", "AIML", "DS"];
  const semesters = [1, 2, 3, 4, 5, 6, 7, 8];
  const schemes = ["2022", "2025"];

  // Check if dark mode is active
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Hide footer
  useEffect(() => {
    setShowFooter(false);
    return () => setShowFooter(true);
  }, [setShowFooter]);

  useEffect(() => {
    const checkDarkMode = () => {
      const dark =
        document.documentElement.getAttribute("data-theme") === "dark";
      setIsDarkMode(dark);
    };
    checkDarkMode();
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login");
      return;
    }

    if (currentUser?._id || currentUser?.id) {
      fetchProfile();
      fetchUserStats();
    }
  }, [isAuthenticated, navigate, currentUser]);

  // Fetch user stats (notes uploads, downloads, likes, views)
  const fetchUserStats = async () => {
    try {
      const token = localStorage.getItem("token");
      const userId = currentUser._id || currentUser.id;

      const notesResponse = await axios.get(
        `/api/notes/user/${userId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      const userNotes = notesResponse.data || [];

      const totalViews = userNotes.reduce(
        (sum, note) => sum + (note.views || 0),
        0,
      );
      const totalDownloads = userNotes.reduce(
        (sum, note) => sum + (note.downloads || 0),
        0,
      );
      const totalLikes = userNotes.reduce(
        (sum, note) => sum + (note.likes?.length || 0),
        0,
      );
      const pendingCount = userNotes.filter(
        (n) => n.status === "pending",
      ).length;
      const approvedCount = userNotes.filter(
        (n) => n.status === "approved",
      ).length;
      const rejectedCount = userNotes.filter(
        (n) => n.status === "rejected",
      ).length;

      setStats({
        totalUploads: userNotes.length,
        totalDownloads,
        totalLikes,
        totalViews,
        pendingCount,
        approvedCount,
        rejectedCount,
      });
    } catch (error) {
      console.error("Fetch user stats error:", error);
    }
  };

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem("token");
      const userId = currentUser._id || currentUser.id;
      const response = await axios.get(
        `/api/users/${userId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      setProfile(response.data);
      setEditForm({
        name: response.data.name,
        branch: response.data.branch,
        semester: response.data.semester,
        scheme: response.data.scheme || "2022",
      });
      setImageError(false);
    } catch (error) {
      console.error("Fetch profile error:", error);
      toast.error("Failed to fetch profile");
      if (error.response?.status === 401) {
        navigate("/login");
      }
    } finally {
      setLoading(false);
    }
  };

  const onDrop = async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;

    const file = acceptedFiles[0];

    const reader = new FileReader();
    reader.onload = (e) => {
      setProfile((prev) => ({
        ...prev,
        profilePicture: e.target.result,
      }));
    };
    reader.readAsDataURL(file);

    const formData = new FormData();
    formData.append("profilePicture", file);

    setUploading(true);
    setImageError(false);

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        "/api/users/profile/picture",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      setProfile(response.data.user);
      dispatch(updateUser(response.data.user));
      toast.success("Profile picture updated successfully");
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(
        error.response?.data?.message || "Failed to upload profile picture",
      );
      fetchProfile();
    } finally {
      setUploading(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "image/*": [".jpeg", ".jpg", ".png", ".gif", ".webp"],
    },
    maxFiles: 1,
    maxSize: 5 * 1024 * 1024,
  });

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("token");
      const response = await axios.put(
        "/api/users/profile",
        editForm,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      setProfile(response.data);
      setIsEditing(false);
      dispatch(updateUser(response.data));
      toast.success("Profile updated successfully");
    } catch (error) {
      console.error("Edit error:", error);
      toast.error(error.response?.data?.message || "Failed to update profile");
    }
  };

  const getProfileImageUrl = () => {
    if (!profile?.profilePicture || imageError) {
      const initials =
        profile?.name
          ?.split(" ")
          .map((n) => n[0])
          .join("")
          .toUpperCase()
          .substring(0, 2) || "U";

      return `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='150' height='150' viewBox='0 0 150 150'%3E%3Crect width='150' height='150' fill='%238b5cf6'/%3E%3Ctext x='75' y='90' font-size='50' text-anchor='middle' fill='white' font-family='Arial'%3E${initials}%3C/text%3E%3C/svg%3E`;
    }

    if (profile.profilePicture.startsWith("data:")) {
      return profile.profilePicture;
    }

    if (profile.profilePicture.startsWith("http")) {
      return profile.profilePicture;
    }

    if (profile.profilePicture.startsWith("/uploads")) {
      return buildApiUrl(profile.profilePicture);
    }

    const initials =
      profile?.name
        ?.split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .substring(0, 2) || "U";
    return `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='150' height='150' viewBox='0 0 150 150'%3E%3Crect width='150' height='150' fill='%238b5cf6'/%3E%3Ctext x='75' y='90' font-size='50' text-anchor='middle' fill='white' font-family='Arial'%3E${initials}%3C/text%3E%3C/svg%3E`;
  };

  const handleImageError = () => {
    if (!imageError) {
      setImageError(true);
    }
  };

  // Helper functions for styling
  const getTextColor = () => (isDarkMode ? "#f1f5f9" : "#1e293b");
  const getTextSecondary = () => (isDarkMode ? "#94a3b8" : "#64748b");
  const getBorderColor = () =>
    isDarkMode ? "rgba(139,92,246,0.2)" : "rgba(139,92,246,0.1)";
  const getCardBackground = () =>
    isDarkMode ? "rgba(30, 30, 45, 0.8)" : "rgba(255, 255, 255, 0.9)";
  const getInputBackground = () => (isDarkMode ? "rgba(0,0,0,0.3)" : "#ffffff");

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 300, damping: 24 },
    },
  };

  if (loading) return <Loader />;
  if (!profile)
    return (
      <div style={{ textAlign: "center", padding: "2rem" }}>
        Profile not found
      </div>
    );

  return (
    <div
      style={{
        minHeight: "calc(100vh - 200px)",
        background: isDarkMode ? "#0a0a0f" : "#f5f7fb",
        padding: isMobile ? "1rem" : "2rem",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
      }}
    >
      {/* Animated Background Grid */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: "none",
          zIndex: 0,
          backgroundImage: `radial-gradient(circle at 1px 1px, ${isDarkMode ? "rgba(139,92,246,0.1)" : "rgba(139,92,246,0.08)"} 1px, transparent 1px)`,
          backgroundSize: "40px 40px",
        }}
      />

      {/* Floating Gradient Orbs */}
      <motion.div
        animate={{ x: [0, 100, 0], y: [0, 50, 0] }}
        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        style={{
          position: "fixed",
          top: "10%",
          left: "-5%",
          width: "250px",
          height: "250px",
          background:
            "radial-gradient(circle, rgba(139,92,246,0.3) 0%, transparent 70%)",
          borderRadius: "50%",
          filter: "blur(60px)",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />
      <motion.div
        animate={{ x: [0, -100, 0], y: [0, 80, 0] }}
        transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
        style={{
          position: "fixed",
          bottom: "10%",
          right: "-5%",
          width: "300px",
          height: "300px",
          background:
            "radial-gradient(circle, rgba(59,130,246,0.3) 0%, transparent 70%)",
          borderRadius: "50%",
          filter: "blur(60px)",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, type: "spring", stiffness: 300 }}
        style={{
          maxWidth: "1000px",
          width: "100%",
          background: getCardBackground(),
          backdropFilter: "blur(20px)",
          borderRadius: "32px",
          overflow: "hidden",
          boxShadow: isDarkMode
            ? "0 25px 50px -12px rgba(0,0,0,0.5)"
            : "0 25px 50px -12px rgba(0,0,0,0.25)",
          border: `1px solid ${getBorderColor()}`,
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* Animated gradient border top */}
        <motion.div
          animate={{
            background: [
              "linear-gradient(90deg, #8b5cf6, #3b82f6, #06b6d4, #8b5cf6)",
              "linear-gradient(90deg, #3b82f6, #06b6d4, #8b5cf6, #3b82f6)",
            ],
          }}
          transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
            zIndex: 2,
          }}
        />

        {/* Cover Photo with Illustration */}
        <div
          style={{
            height: "200px",
            background: "linear-gradient(135deg, #1e1b4b 0%, #2e1065 100%)",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <motion.div
            animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.1, 0.3] }}
            transition={{ duration: 4, repeat: Infinity }}
            style={{
              position: "absolute",
              top: -50,
              right: -50,
              width: 200,
              height: 200,
              background:
                "radial-gradient(circle, rgba(139,92,246,0.4) 0%, transparent 70%)",
              borderRadius: "50%",
            }}
          />

          {/* Profile Illustration SVG */}
          <motion.div
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            style={{
              position: "absolute",
              bottom: "20px",
              left: "30px",
              opacity: 0.6,
            }}
          >
            <svg width="100" height="100" viewBox="0 0 100 100" fill="none">
              <circle
                cx="50"
                cy="40"
                r="25"
                fill="rgba(139,92,246,0.3)"
                stroke="#8b5cf6"
                strokeWidth="1.5"
              />
              <circle cx="50" cy="40" r="15" fill="rgba(139,92,246,0.2)" />
              <circle cx="42" cy="36" r="2" fill="#8b5cf6" />
              <circle cx="58" cy="36" r="2" fill="#8b5cf6" />
              <path
                d="M45 46 Q50 52 55 46"
                stroke="#8b5cf6"
                strokeWidth="1.5"
                fill="none"
                strokeLinecap="round"
              />
              <rect
                x="40"
                y="60"
                width="20"
                height="25"
                rx="4"
                fill="rgba(139,92,246,0.3)"
                stroke="#8b5cf6"
                strokeWidth="1.5"
              />
              <path
                d="M40 65 L30 58"
                stroke="#8b5cf6"
                strokeWidth="2"
                strokeLinecap="round"
              />
              <path
                d="M60 65 L70 58"
                stroke="#8b5cf6"
                strokeWidth="2"
                strokeLinecap="round"
              />
              <text
                x="80"
                y="35"
                fontSize="20"
                fill="#f59e0b"
                fontWeight="bold"
              >
                ⭐
              </text>
              <text x="10" y="45" fontSize="16" fill="#3b82f6">
                ✨
              </text>
              <circle cx="25" cy="25" r="2" fill="#8b5cf6" />
              <circle cx="75" cy="28" r="1.5" fill="#3b82f6" />
            </svg>
          </motion.div>

          <div
            style={{
              position: "absolute",
              bottom: "20px",
              right: "20px",
              display: "flex",
              gap: "10px",
              zIndex: 10,
            }}
          >
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setIsEditing(true)}
              style={{
                padding: "0.6rem 1.5rem",
                background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                color: "white",
                border: "none",
                borderRadius: "40px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                fontSize: "0.9rem",
                fontWeight: "600",
                boxShadow: "0 4px 14px 0 rgba(139,92,246,0.4)",
              }}
            >
              <FaEdit size={14} /> Edit Profile
            </motion.button>
          </div>
        </div>

        {/* Profile Content */}
        <div style={{ padding: "0 2rem 2rem", position: "relative" }}>
          {/* Profile Picture */}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginTop: "-75px",
              marginBottom: "2rem",
              position: "relative",
            }}
          >
            <div style={{ position: "relative" }}>
              <motion.div
                whileHover={{ scale: 1.02 }}
                style={{
                  position: "relative",
                  borderRadius: "50%",
                  boxShadow: "0 20px 40px -12px rgba(139,92,246,0.4)",
                }}
              >
                <img
                  src={getProfileImageUrl()}
                  alt={profile.name}
                  onError={handleImageError}
                  style={{
                    width: "150px",
                    height: "150px",
                    borderRadius: "50%",
                    objectFit: "cover",
                    border: "4px solid rgba(139, 92, 246, 0.3)",
                    background: "#f0f0f0",
                  }}
                />
              </motion.div>

              <motion.div
                {...getRootProps()}
                whileHover={{ scale: uploading ? 1 : 1.1 }}
                whileTap={{ scale: uploading ? 1 : 0.95 }}
                style={{
                  position: "absolute",
                  bottom: "10px",
                  right: "10px",
                  width: "40px",
                  height: "40px",
                  background: uploading
                    ? "#6c757d"
                    : "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "white",
                  cursor: uploading ? "not-allowed" : "pointer",
                  border: "3px solid white",
                  boxShadow: "0 4px 14px 0 rgba(0,0,0,0.2)",
                  transition: "all 0.2s",
                  zIndex: 20,
                }}
              >
                <input {...getInputProps()} disabled={uploading} />
                {uploading ? (
                  <div
                    style={{
                      width: "20px",
                      height: "20px",
                      border: "2px solid white",
                      borderTopColor: "transparent",
                      borderRadius: "50%",
                      animation: "spin 1s linear infinite",
                    }}
                  />
                ) : (
                  <FaCamera size={18} />
                )}
              </motion.div>
            </div>

            {isDragActive && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                style={{
                  position: "absolute",
                  top: "-75px",
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background:
                    "linear-gradient(135deg, rgba(139,92,246,0.95), rgba(59,130,246,0.95))",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "20px",
                  color: "white",
                  fontSize: "1.2rem",
                  fontWeight: "bold",
                  zIndex: 10,
                  pointerEvents: "none",
                  backdropFilter: "blur(10px)",
                }}
              >
                <HiOutlineSparkles size={24} style={{ marginRight: "10px" }} />
                Drop your image here
              </motion.div>
            )}
          </div>

          {/* Profile Info */}
          <AnimatePresence mode="wait">
            {isEditing ? (
              <motion.div
                key="edit"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.3 }}
                style={{
                  background: isDarkMode
                    ? "rgba(20, 20, 35, 0.6)"
                    : "rgba(248, 250, 252, 0.8)",
                  backdropFilter: "blur(10px)",
                  padding: "2rem",
                  borderRadius: "24px",
                  marginBottom: "2rem",
                  border: `1px solid ${getBorderColor()}`,
                }}
              >
                <h3
                  style={{
                    background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    marginBottom: "1.5rem",
                    fontSize: "1.3rem",
                    fontWeight: 600,
                  }}
                >
                  Edit Profile
                </h3>
                <form onSubmit={handleEditSubmit}>
                  <div style={{ marginBottom: "1.5rem" }}>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "0.5rem",
                        color: getTextSecondary(),
                        fontWeight: "500",
                      }}
                    >
                      Full Name
                    </label>
                    <input
                      type="text"
                      value={editForm.name}
                      onChange={(e) =>
                        setEditForm({ ...editForm, name: e.target.value })
                      }
                      required
                      style={{
                        width: "100%",
                        padding: "0.8rem 1rem",
                        border: `2px solid ${getBorderColor()}`,
                        borderRadius: "12px",
                        outline: "none",
                        fontSize: "1rem",
                        transition: "all 0.3s",
                        backgroundColor: getInputBackground(),
                        color: getTextColor(),
                      }}
                      onFocus={(e) => (e.target.style.borderColor = "#8b5cf6")}
                      onBlur={(e) =>
                        (e.target.style.borderColor = getBorderColor())
                      }
                    />
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "1rem",
                      marginBottom: "1.5rem",
                    }}
                  >
                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "0.5rem",
                          color: getTextSecondary(),
                          fontWeight: "500",
                        }}
                      >
                        Branch
                      </label>
                      <select
                        value={editForm.branch}
                        onChange={(e) =>
                          setEditForm({ ...editForm, branch: e.target.value })
                        }
                        required
                        style={{
                          width: "100%",
                          padding: "0.8rem 1rem",
                          border: `2px solid ${getBorderColor()}`,
                          borderRadius: "12px",
                          outline: "none",
                          fontSize: "1rem",
                          backgroundColor: getInputBackground(),
                          color: getTextColor(),
                        }}
                      >
                        <option value="">Select Branch</option>
                        {branches.map((branch) => (
                          <option key={branch} value={branch}>
                            {branch}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "0.5rem",
                          color: getTextSecondary(),
                          fontWeight: "500",
                        }}
                      >
                        Semester
                      </label>
                      <select
                        value={editForm.semester}
                        onChange={(e) =>
                          setEditForm({ ...editForm, semester: e.target.value })
                        }
                        required
                        style={{
                          width: "100%",
                          padding: "0.8rem 1rem",
                          border: `2px solid ${getBorderColor()}`,
                          borderRadius: "12px",
                          outline: "none",
                          fontSize: "1rem",
                          backgroundColor: getInputBackground(),
                          color: getTextColor(),
                        }}
                      >
                        <option value="">Select Semester</option>
                        {semesters.map((sem) => (
                          <option key={sem} value={sem}>
                            {sem}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div style={{ marginBottom: "1.5rem" }}>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "0.5rem",
                        color: getTextSecondary(),
                        fontWeight: "500",
                      }}
                    >
                      <FaCalendarAlt
                        style={{ marginRight: "0.5rem", color: "#8b5cf6" }}
                      />
                      Scheme
                    </label>
                    <select
                      value={editForm.scheme}
                      onChange={(e) =>
                        setEditForm({ ...editForm, scheme: e.target.value })
                      }
                      required
                      style={{
                        width: "100%",
                        padding: "0.8rem 1rem",
                        border: `2px solid ${getBorderColor()}`,
                        borderRadius: "12px",
                        outline: "none",
                        fontSize: "1rem",
                        backgroundColor: getInputBackground(),
                        color: getTextColor(),
                      }}
                    >
                      <option value="">Select Scheme</option>
                      {schemes.map((scheme) => (
                        <option key={scheme} value={scheme}>
                          {scheme === "2022"
                            ? "CBCS 2022 Scheme"
                            : "CBCS 2025 Scheme"}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      gap: "1rem",
                      justifyContent: "flex-end",
                    }}
                  >
                    <motion.button
                      type="button"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setIsEditing(false)}
                      style={{
                        padding: "0.8rem 2rem",
                        background: isDarkMode
                          ? "rgba(100, 116, 139, 0.3)"
                          : "#e2e8f0",
                        color: getTextColor(),
                        border: "none",
                        borderRadius: "12px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                        fontSize: "1rem",
                        fontWeight: "500",
                      }}
                    >
                      <FaTimes /> Cancel
                    </motion.button>
                    <motion.button
                      type="submit"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      style={{
                        padding: "0.8rem 2rem",
                        background: "linear-gradient(135deg, #10b981, #059669)",
                        color: "white",
                        border: "none",
                        borderRadius: "12px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                        fontSize: "1rem",
                        fontWeight: "500",
                      }}
                    >
                      <FaSave /> Save Changes
                    </motion.button>
                  </div>
                </form>
              </motion.div>
            ) : (
              <motion.div
                key="view"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                style={{ textAlign: "center", marginBottom: "2rem" }}
              >
                <motion.h1
                  initial={{ scale: 0.9 }}
                  animate={{ scale: 1 }}
                  style={{
                    fontSize: "2rem",
                    background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    marginBottom: "0.5rem",
                    fontWeight: 700,
                  }}
                >
                  {profile.name}
                </motion.h1>
                <p
                  style={{
                    color: "#8b5cf6",
                    fontSize: "1rem",
                    marginBottom: "1rem",
                    fontWeight: "500",
                  }}
                >
                  {profile.branch} • Semester {profile.semester} • Scheme{" "}
                  {profile.scheme || "2022"}
                </p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Details Grid - Email, USN, Branch, Member Since */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)",
              gap: "1rem",
              marginBottom: "2rem",
            }}
          >
            {[
              {
                icon: <FaEnvelope />,
                label: "Email",
                value: profile.email,
                color: "#8b5cf6",
              },
              {
                icon: <FaIdCard />,
                label: "USN",
                value: profile.usn,
                color: "#3b82f6",
              },
              {
                icon: <FaGraduationCap />,
                label: "Branch",
                value: profile.branch,
                color: "#06b6d4",
              },
              {
                icon: <FaCalendarAlt />,
                label: "Member Since",
                value: new Date(profile.createdAt).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                }),
                color: "#f59e0b",
              },
            ].map((detail, idx) => (
              <motion.div
                key={idx}
                variants={itemVariants}
                whileHover={{ x: 5 }}
                style={{
                  background: getCardBackground(),
                  backdropFilter: "blur(20px)",
                  borderRadius: "16px",
                  padding: "1rem",
                  display: "flex",
                  alignItems: "center",
                  gap: "1rem",
                  border: `1px solid ${getBorderColor()}`,
                }}
              >
                <div
                  style={{
                    width: "45px",
                    height: "45px",
                    background: `${detail.color}15`,
                    borderRadius: "12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: detail.color,
                    fontSize: "1.2rem",
                  }}
                >
                  {detail.icon}
                </div>
                <div>
                  <div
                    style={{ color: getTextSecondary(), fontSize: "0.7rem" }}
                  >
                    {detail.label}
                  </div>
                  <div
                    style={{
                      color: getTextColor(),
                      fontWeight: "500",
                      fontSize: "0.85rem",
                      wordBreak: "break-all",
                    }}
                  >
                    {detail.value}
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Stats Cards - MOVED TO BOTTOM (After Details Grid) */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            style={{
              display: "grid",
              gridTemplateColumns: isMobile
                ? "repeat(2, 1fr)"
                : "repeat(4, 1fr)",
              gap: "1rem",
              marginBottom: "2rem",
            }}
          >
            <motion.div
              variants={itemVariants}
              whileHover={{ y: -3, scale: 1.02 }}
              transition={{ type: "spring", stiffness: 400, damping: 17 }}
              style={{
                background: getCardBackground(),
                backdropFilter: "blur(20px)",
                borderRadius: "20px",
                padding: "1.2rem",
                textAlign: "center",
                border: `1px solid ${getBorderColor()}`,
                cursor: "pointer",
              }}
            >
              <div
                style={{
                  width: "48px",
                  height: "48px",
                  background: "rgba(139,92,246,0.1)",
                  borderRadius: "14px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 0.8rem auto",
                }}
              >
                <FiUploadCloud size={24} color="#8b5cf6" />
              </div>
              <div
                style={{
                  fontSize: "1.8rem",
                  fontWeight: "bold",
                  color: getTextColor(),
                }}
              >
                {stats.totalUploads}
              </div>
              <div
                style={{
                  fontSize: "0.75rem",
                  color: getTextSecondary(),
                  marginTop: "0.25rem",
                }}
              >
                Notes Uploaded
              </div>
            </motion.div>

            <motion.div
              variants={itemVariants}
              whileHover={{ y: -3, scale: 1.02 }}
              transition={{ type: "spring", stiffness: 400, damping: 17 }}
              style={{
                background: getCardBackground(),
                backdropFilter: "blur(20px)",
                borderRadius: "20px",
                padding: "1.2rem",
                textAlign: "center",
                border: `1px solid ${getBorderColor()}`,
                cursor: "pointer",
              }}
            >
              <div
                style={{
                  width: "48px",
                  height: "48px",
                  background: "rgba(16,185,129,0.1)",
                  borderRadius: "14px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 0.8rem auto",
                }}
              >
                <FiDownload size={24} color="#10b981" />
              </div>
              <div
                style={{
                  fontSize: "1.8rem",
                  fontWeight: "bold",
                  color: getTextColor(),
                }}
              >
                {stats.totalDownloads}
              </div>
              <div
                style={{
                  fontSize: "0.75rem",
                  color: getTextSecondary(),
                  marginTop: "0.25rem",
                }}
              >
                Total Downloads
              </div>
            </motion.div>

            <motion.div
              variants={itemVariants}
              whileHover={{ y: -3, scale: 1.02 }}
              transition={{ type: "spring", stiffness: 400, damping: 17 }}
              style={{
                background: getCardBackground(),
                backdropFilter: "blur(20px)",
                borderRadius: "20px",
                padding: "1.2rem",
                textAlign: "center",
                border: `1px solid ${getBorderColor()}`,
                cursor: "pointer",
              }}
            >
              <div
                style={{
                  width: "48px",
                  height: "48px",
                  background: "rgba(239,68,68,0.1)",
                  borderRadius: "14px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 0.8rem auto",
                }}
              >
                <FaHeart size={24} color="#ef4444" />
              </div>
              <div
                style={{
                  fontSize: "1.8rem",
                  fontWeight: "bold",
                  color: getTextColor(),
                }}
              >
                {stats.totalLikes}
              </div>
              <div
                style={{
                  fontSize: "0.75rem",
                  color: getTextSecondary(),
                  marginTop: "0.25rem",
                }}
              >
                Total Likes
              </div>
            </motion.div>

            <motion.div
              variants={itemVariants}
              whileHover={{ y: -3, scale: 1.02 }}
              transition={{ type: "spring", stiffness: 400, damping: 17 }}
              style={{
                background: getCardBackground(),
                backdropFilter: "blur(20px)",
                borderRadius: "20px",
                padding: "1.2rem",
                textAlign: "center",
                border: `1px solid ${getBorderColor()}`,
                cursor: "pointer",
              }}
            >
              <div
                style={{
                  width: "48px",
                  height: "48px",
                  background: "rgba(59,130,246,0.1)",
                  borderRadius: "14px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 0.8rem auto",
                }}
              >
                <FaEye size={24} color="#3b82f6" />
              </div>
              <div
                style={{
                  fontSize: "1.8rem",
                  fontWeight: "bold",
                  color: getTextColor(),
                }}
              >
                {stats.totalViews}
              </div>
              <div
                style={{
                  fontSize: "0.75rem",
                  color: getTextSecondary(),
                  marginTop: "0.25rem",
                }}
              >
                Total Views
              </div>
            </motion.div>
          </motion.div>

          {/* Security Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            style={{
              marginTop: "1rem",
              padding: "0.8rem",
              textAlign: "center",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "0.5rem",
              background: isDarkMode
                ? "rgba(139,92,246,0.05)"
                : "rgba(139,92,246,0.03)",
              borderRadius: "40px",
              border: `1px solid ${getBorderColor()}`,
            }}
          >
            <FaShieldAlt size={12} color="#10b981" />
            <span style={{ color: getTextSecondary(), fontSize: "0.7rem" }}>
              Your profile information is securely protected
            </span>
            <FaRocket size={12} color="#8b5cf6" />
          </motion.div>
        </div>
      </motion.div>

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes float {
            0%, 100% { transform: translate(0, 0) rotate(0deg); }
            50% { transform: translate(20px, 20px) rotate(10deg); }
          }
        `}
      </style>
    </div>
  );
};

export default Profile;

